import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Coins,
  Loader2,
  Percent,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  ShieldAlert,
  Users,
} from 'lucide-react';
import {
  referralAdminAPI,
  type AdminReferralRebateRecord,
  type ReferralRebateConfig,
  type ReferralRebateStats,
} from '../../services/api';
import { formatBeijingDateTime } from '../../utils/time';

type StatusFilter = '' | 'credited' | 'blocked' | 'reversed';

const STATUS_LABELS: Record<string, string> = {
  credited: '已发放',
  blocked: '风控拦截',
  reversed: '已冲销',
};

const STATUS_CLASSES: Record<string, string> = {
  credited: 'bg-green-50 text-green-600',
  blocked: 'bg-amber-50 text-amber-600',
  reversed: 'bg-light-hover text-light-muted',
};

const BLOCK_REASON_LABELS: Record<string, string> = {
  same_register_ip: '同注册 IP',
  same_device_fingerprint: '同设备指纹',
  same_login_ip: '同登录 IP',
  monthly_cap: '月度上限',
  inviter_inactive: '邀请人已封禁',
};

interface ConfigForm {
  enabled: boolean;
  rebatePercent: string;
  minOrderAmount: string;
  maxCreditsPerOrder: string;
  monthlyCapCredits: string;
  blockSameIp: boolean;
  blockSameDevice: boolean;
  blockSameLoginIp: boolean;
  publicIpUserThreshold: string;
}

const EMPTY_CONFIG_FORM: ConfigForm = {
  enabled: false,
  rebatePercent: '10',
  minOrderAmount: '20',
  maxCreditsPerOrder: '1000',
  monthlyCapCredits: '0',
  blockSameIp: true,
  blockSameDevice: true,
  blockSameLoginIp: true,
  publicIpUserThreshold: '3',
};

function toConfigForm(config: ReferralRebateConfig): ConfigForm {
  return {
    enabled: config.enabled,
    rebatePercent: String(config.rebatePercent),
    minOrderAmount: String(config.minOrderAmount),
    maxCreditsPerOrder: String(config.maxCreditsPerOrder),
    monthlyCapCredits: String(config.monthlyCapCredits),
    blockSameIp: config.blockSameIp,
    blockSameDevice: config.blockSameDevice,
    blockSameLoginIp: config.blockSameLoginIp,
    publicIpUserThreshold: String(config.publicIpUserThreshold),
  };
}

function formatDisplayName(name: string | null, email: string): string {
  return name?.trim() || email;
}

export default function ReferralRebateTab() {
  const [config, setConfig] = useState<ReferralRebateConfig | null>(null);
  const [form, setForm] = useState<ConfigForm>(EMPTY_CONFIG_FORM);
  const [stats, setStats] = useState<ReferralRebateStats | null>(null);
  const [rebates, setRebates] = useState<AdminReferralRebateRecord[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const loadConfigAndStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [configData, statsData] = await Promise.all([
        referralAdminAPI.getConfig(),
        referralAdminAPI.getStats(),
      ]);
      setConfig(configData);
      setForm(toConfigForm(configData));
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载推广返利配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRebates = useCallback(
    async (page = 1) => {
      setListLoading(true);
      try {
        const res = await referralAdminAPI.getRebates({
          page,
          limit: pagination.limit,
          status: statusFilter,
          search,
        });
        setRebates(res.data);
        setPagination(res.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : '读取返利流水失败');
      } finally {
        setListLoading(false);
      }
    },
    [pagination.limit, statusFilter, search]
  );

  useEffect(() => {
    void loadConfigAndStats();
  }, [loadConfigAndStats]);

  useEffect(() => {
    void loadRebates(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search]);

  const setField = <K extends keyof ConfigForm>(key: K, value: ConfigForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await referralAdminAPI.updateConfig({
        enabled: form.enabled,
        rebatePercent: Number(form.rebatePercent),
        minOrderAmount: Number(form.minOrderAmount),
        maxCreditsPerOrder: Number(form.maxCreditsPerOrder),
        monthlyCapCredits: Number(form.monthlyCapCredits),
        blockSameIp: form.blockSameIp,
        blockSameDevice: form.blockSameDevice,
        blockSameLoginIp: form.blockSameLoginIp,
        publicIpUserThreshold: Number(form.publicIpUserThreshold),
      });
      setConfig(updated);
      setForm(toConfigForm(updated));
      setNotice('配置已保存，下一次支付回调立即生效');
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    setError(null);
    setNotice(null);
    try {
      const result = await referralAdminAPI.reconcile(7);
      setNotice(
        `补算完成：扫描 ${result.scanned} 笔订单，补发 ${result.credited} 笔，跳过 ${result.skipped} 笔`
      );
      await Promise.all([loadConfigAndStats(), loadRebates(1)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : '补算失败');
    } finally {
      setRecalculating(false);
    }
  };

  const creditsPerYuan = Number(form.rebatePercent) > 0
    ? (Number(form.rebatePercent) / 100) * (config?.exchangeRate ?? 10)
    : 0;
  const sampleCredits = Math.floor(100 * creditsPerYuan + 1e-6);

  return (
    <div className="space-y-4 lg:space-y-6">
      {error && (
        <div className="flex items-start gap-2 bg-red-50 text-red-600 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="flex items-start gap-2 bg-green-50 text-green-600 rounded-xl px-4 py-3 text-sm">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {/* 配置卡 */}
      <div className="bg-white border border-light-border rounded-2xl shadow-sm">
        <div className="p-4 lg:p-5 border-b border-light-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent-blue/10 text-accent-blue flex items-center justify-center shrink-0">
              <Percent className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-light-text">充值返利</h3>
              <p className="text-sm text-light-muted mt-1">
                好友通过邀请链接注册后，每笔真实充值按实付金额返给邀请人积分
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-xl hover:opacity-90 transition-opacity text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            保存配置
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-light-muted gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            加载中
          </div>
        ) : (
          <div className="p-4 lg:p-5 space-y-5">
            <label className="flex items-start gap-3 p-4 bg-light-hover rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setField('enabled', e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-accent-blue"
              />
              <div>
                <p className="text-sm font-medium text-light-text">启用充值返利</p>
                <p className="text-xs text-light-muted mt-1">
                  关闭后不再产生新的返利记录，已有记录与冲销逻辑保持不变
                </p>
              </div>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">返利率</label>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={form.rebatePercent}
                    onChange={(e) => setField('rebatePercent', e.target.value)}
                    className="w-full bg-white border border-light-border rounded-xl px-4 py-2.5 pr-9 text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted text-sm">%</span>
                </div>
                <p className="text-xs text-light-muted mt-1">
                  按实付金额计算，套餐赠送积分不参与
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">每元返积分</label>
                <div className="px-4 py-2.5 bg-light-hover rounded-xl text-light-text text-sm">
                  {creditsPerYuan.toFixed(2)} 积分 / 元
                </div>
                <p className="text-xs text-light-muted mt-1">
                  好友充 ¥100 返 {sampleCredits} 积分（汇率 {config?.exchangeRate ?? 10} 积分/元）
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">起返金额</label>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={form.minOrderAmount}
                    onChange={(e) => setField('minOrderAmount', e.target.value)}
                    className="w-full bg-white border border-light-border rounded-xl px-4 py-2.5 pr-9 text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted text-sm">元</span>
                </div>
                <p className="text-xs text-light-muted mt-1">低于该金额的订单不返利</p>
              </div>

              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">单笔上限</label>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    step={10}
                    value={form.maxCreditsPerOrder}
                    onChange={(e) => setField('maxCreditsPerOrder', e.target.value)}
                    className="w-full bg-white border border-light-border rounded-xl px-4 py-2.5 pr-12 text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted text-sm">积分</span>
                </div>
                <p className="text-xs text-light-muted mt-1">单笔订单最多返这么多积分，填 0 表示不限</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">月度上限</label>
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    step={100}
                    value={form.monthlyCapCredits}
                    onChange={(e) => setField('monthlyCapCredits', e.target.value)}
                    className="w-full bg-white border border-light-border rounded-xl px-4 py-2.5 pr-12 text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted text-sm">积分</span>
                </div>
                <p className="text-xs text-light-muted mt-1">单个邀请人自然月累计返利上限，填 0 表示不限</p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-light-text">风控</p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.blockSameIp}
                    onChange={(e) => setField('blockSameIp', e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-accent-blue"
                  />
                  <span className="text-sm text-light-text">
                    同注册 IP 不返
                    <span className="block text-xs text-light-muted">
                      邀请人与被邀请人注册 IP 相同时拦截，记录保留供人工复核
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.blockSameDevice}
                    onChange={(e) => setField('blockSameDevice', e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-accent-blue"
                  />
                  <span className="text-sm text-light-text">
                    同设备指纹不返
                    <span className="block text-xs text-light-muted">
                      邀请人与被邀请人设备指纹相同时拦截
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.blockSameLoginIp}
                    onChange={(e) => setField('blockSameLoginIp', e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-accent-blue"
                  />
                  <span className="text-sm text-light-text">
                    同登录 IP 不返
                    <span className="block text-xs text-light-muted">
                      比对双方近半年登录记录，注册 IP 不同也可能命中
                    </span>
                  </span>
                </label>
                {form.blockSameLoginIp && (
                  <div>
                    <label className="text-xs font-medium text-light-text mb-1 block">
                      公共出口豁免阈值
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={form.publicIpUserThreshold}
                        onChange={(e) => setField('publicIpUserThreshold', e.target.value)}
                        className="w-full bg-white border border-light-border rounded-xl px-3 py-2 pr-16 text-sm text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted text-xs">
                        个账号
                      </span>
                    </div>
                    <p className="text-xs text-light-muted mt-1">
                      一个 IP 被超过该数量的账号使用时视为运营商 NAT，不作为证据；填 0 表示不豁免
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-2 p-4 bg-light-hover rounded-xl text-xs text-light-muted">
              <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p>返利基数取订单实付金额，不取到账积分，因此调整套餐赠送倍数不会放大返利成本。</p>
                <p>订单退款时按退款金额比例冲销返利；邀请人余额不足时扣到零，差额记入对账差额，不产生负余额。</p>
                <p>返利依赖注册时绑定的邀请关系：系统配置里的「邀请奖励」未启用时，新注册不会绑定邀请人，返利无法产生。</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 统计 */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">累计返利积分</p>
          <p className="text-xl lg:text-2xl font-bold text-light-text mt-2">
            {stats?.totalCredits ?? 0}
          </p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">返利订单数</p>
          <p className="text-xl lg:text-2xl font-bold text-light-text mt-2">
            {stats?.totalOrders ?? 0}
          </p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">本月返利积分</p>
          <p className="text-xl lg:text-2xl font-bold text-accent-blue mt-2">
            {stats?.monthCredits ?? 0}
          </p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">已冲销 / 差额</p>
          <p className="text-xl lg:text-2xl font-bold text-light-text mt-2">
            {stats?.reversedCredits ?? 0}
            <span className="text-sm font-normal text-light-muted ml-1">
              / {stats?.shortfallCredits ?? 0}
            </span>
          </p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">风控拦截 / 待补算</p>
          <p className="text-xl lg:text-2xl font-bold text-light-text mt-2">
            {stats?.blockedOrders ?? 0}
            <span className="text-sm font-normal text-light-muted ml-1">
              / {stats?.pendingRecalc ?? 0}
            </span>
          </p>
        </div>
      </div>

      {/* 返利流水 */}
      <div className="bg-white border border-light-border rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 lg:p-5 border-b border-light-border flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-light-text">返利流水</h3>
              <p className="text-sm text-light-muted mt-1">
                共 {pagination.total} 条记录，可核对实付金额、比例与发放状态
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRecalculate}
                disabled={recalculating}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors text-sm disabled:opacity-50"
                title="扫描近 7 天已支付但没有返利记录的订单"
              >
                {recalculating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                补算近 7 天
              </button>
              <button
                onClick={() => {
                  void loadConfigAndStats();
                  void loadRebates(pagination.page);
                }}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                刷新
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="bg-white border border-light-border rounded-xl px-3 py-2 text-sm text-light-text focus:outline-none focus:border-accent-blue"
            >
              <option value="">全部状态</option>
              <option value="credited">已发放</option>
              <option value="blocked">风控拦截</option>
              <option value="reversed">已冲销</option>
            </select>
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-light-muted absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setSearch(searchInput.trim());
                }}
                placeholder="搜索邀请人 / 被邀请人 / 订单号，回车确认"
                className="w-full bg-white border border-light-border rounded-xl pl-9 pr-3 py-2 text-sm text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue"
              />
            </div>
            <button
              onClick={() => setSearch(searchInput.trim())}
              className="px-4 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors text-sm"
            >
              搜索
            </button>
          </div>
        </div>

        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-light-hover text-left text-sm text-light-muted">
                <th className="px-5 py-3 font-medium">邀请人</th>
                <th className="px-5 py-3 font-medium">被邀请人</th>
                <th className="px-5 py-3 font-medium">订单号</th>
                <th className="px-5 py-3 font-medium">实付金额</th>
                <th className="px-5 py-3 font-medium">比例</th>
                <th className="px-5 py-3 font-medium">返利积分</th>
                <th className="px-5 py-3 font-medium">状态</th>
                <th className="px-5 py-3 font-medium">时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light-border">
              {listLoading ? (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center text-light-muted">加载中...</td>
                </tr>
              ) : rebates.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center text-light-muted">暂无返利记录</td>
                </tr>
              ) : (
                rebates.map((item) => (
                  <tr key={item.id} className="hover:bg-light-hover/50 transition-colors">
                    <td className="px-5 py-4 text-sm">
                      <p className="font-medium text-light-text">
                        {formatDisplayName(item.inviterName, item.inviterEmail)}
                      </p>
                      <p className="text-xs text-light-muted">{item.inviterEmail}</p>
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <p className="text-light-text">
                        {formatDisplayName(item.inviteeName, item.inviteeEmail)}
                      </p>
                      <p className="text-xs text-light-muted">{item.inviteeEmail}</p>
                    </td>
                    <td className="px-5 py-4 text-xs text-light-muted font-mono">
                      {item.outTradeNo}
                    </td>
                    <td className="px-5 py-4 text-sm text-light-text">¥{item.baseAmount.toFixed(2)}</td>
                    <td className="px-5 py-4 text-sm text-light-muted">{item.percent}%</td>
                    <td className="px-5 py-4 text-sm font-medium text-green-600">
                      +{item.credits}
                      {item.reversedCredits > 0 && (
                        <span className="block text-xs font-normal text-light-muted">
                          已冲销 {item.reversedCredits}
                          {item.shortfallCredits > 0 && `，差额 ${item.shortfallCredits}`}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${
                          STATUS_CLASSES[item.status] ?? 'bg-light-hover text-light-muted'
                        }`}
                      >
                        {STATUS_LABELS[item.status] ?? item.status}
                      </span>
                      {item.blockReason && (
                        <span className="block text-xs text-light-muted mt-1">
                          {BLOCK_REASON_LABELS[item.blockReason] ?? item.blockReason}
                          {item.blockDetail ? `：${item.blockDetail}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-xs text-light-muted whitespace-nowrap">
                      {formatBeijingDateTime(item.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="lg:hidden divide-y divide-light-border">
          {listLoading ? (
            <div className="px-5 py-10 text-center text-light-muted">加载中...</div>
          ) : rebates.length === 0 ? (
            <div className="px-5 py-10 text-center text-light-muted">暂无返利记录</div>
          ) : (
            rebates.map((item) => (
              <div key={item.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-light-text">
                      {formatDisplayName(item.inviterName, item.inviterEmail)}
                    </p>
                    <p className="text-xs text-light-muted mt-0.5">邀请人</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-600">+{item.credits} 积分</p>
                    <p className="text-xs text-light-muted mt-0.5">¥{item.baseAmount.toFixed(2)} × {item.percent}%</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-light-hover rounded-lg p-3">
                    <p className="text-xs text-light-muted">被邀请人</p>
                    <p className="text-xs text-light-text mt-1 break-all">
                      {formatDisplayName(item.inviteeName, item.inviteeEmail)}
                    </p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-3">
                    <p className="text-xs text-light-muted">状态</p>
                    <p className="text-xs text-light-text mt-1">
                      {STATUS_LABELS[item.status] ?? item.status}
                      {item.blockReason
                        ? `（${BLOCK_REASON_LABELS[item.blockReason] ?? item.blockReason}${
                            item.blockDetail ? `：${item.blockDetail}` : ''
                          }）`
                        : ''}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-light-muted font-mono">{item.outTradeNo}</span>
                  <span className="text-light-text">{formatBeijingDateTime(item.createdAt)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="p-4 border-t border-light-border flex items-center justify-between">
            <p className="text-sm text-light-muted">
              第 {pagination.page} / {pagination.totalPages} 页
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => void loadRebates(pagination.page - 1)}
                disabled={pagination.page <= 1 || listLoading}
                className="px-3 py-1.5 bg-light-hover text-light-text rounded-lg text-sm disabled:opacity-40"
              >
                上一页
              </button>
              <button
                onClick={() => void loadRebates(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || listLoading}
                className="px-3 py-1.5 bg-light-hover text-light-text rounded-lg text-sm disabled:opacity-40"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs text-light-muted">
        <Coins className="w-4 h-4" />
        返利积分发放到邀请人账户，可在用户中心「邀请好友赚积分」查看明细
        <Users className="w-4 h-4 ml-2" />
        仅一级邀请关系参与返利
      </div>
    </div>
  );
}
